import { Box } from '@air/marketing-primitives';
import { includes, without } from 'lodash';
import Image from 'next/image';
import { useMemo, useState } from 'react';

import { MediaData, SectionData } from '~/lib/contentful/custom-types';
import { Container } from '~/old-marketing/components/globals/Container';
import { Section } from '~/old-marketing/components/sections/Section';
import { SectionContent } from '~/old-marketing/components/sections/SectionContent';
import { SectionTitle } from '~/old-marketing/components/sections/SectionTitle';
import { Filters } from '~/old-marketing/components/sections/templates/OperatorsSectionTemplate/components/Filters';
import { OperatorCard } from '~/old-marketing/components/sections/templates/OperatorsSectionTemplate/components/OperatorCard';
import { contentfulImageLoader } from '~/utils/contentfulImageLoader';

const TEMPLATE_KEY = 'operators';

export type OperatorsSectionTemplateProps = Pick<SectionData, 'content' | 'id' | 'title'> & {
  assets?: MediaData[];
  references?: {
    fund?: string;
    id: string;
    industry: string;
    name: string;
    participation: string[];
    url?: string;
  }[];
};

export const OperatorsSectionTemplate = ({
  assets: logos,
  content,
  id,
  references: operators,
  title,
}: OperatorsSectionTemplateProps) => {
  const [filters, setFilters] = useState<string[]>([]);
  const hasFilters = filters.length > 0;

  const filteredOperators = useMemo(() => {
    if (hasFilters && operators) {
      return operators?.filter((operator) => {
        let hasMatch = false;

        if (operator.industry) {
          for (const industry of operator.industry) {
            if (includes(filters, industry)) {
              hasMatch = true;
            }
          }
        }

        return hasMatch;
      });
    }

    return operators;
  }, [filters, hasFilters, operators]);

  const onChangeFilter = (value: string) => {
    if (value === 'all') {
      setFilters([]);
    } else if (includes(filters, value)) {
      setFilters(without(filters, value));
    } else {
      setFilters([value]);
    }
  };

  return (
    <Section css={{ paddingTop: 72, paddingBottom: 108 }} id={id} templateKey={TEMPLATE_KEY}>
      <Container css={{ maxWidth: 864, textAlign: 'center' }}>
        {title && (
          <SectionTitle css={{ color: '$jay800', marginBottom: 36 }} size="heading-40">
            {title}
          </SectionTitle>
        )}

        {content && <SectionContent content={content} />}

        {logos && (
          <Box css={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 64 }}>
            {logos.map((logo, index) => {
              return (
                <Box css={{ display: 'flex', alignItems: 'center', marginX: 16, marginBottom: 24 }} key={index}>
                  <Image
                    height={logo.file.details.image.height}
                    // layout="fill"
                    loader={contentfulImageLoader}
                    // objectFit="cover"
                    src={logo.file.url}
                    width={logo.file.details.image.width}
                  />
                </Box>
              );
            })}
          </Box>
        )}
      </Container>
      <Container>
        <Filters activeFilters={filters} onChangeFilter={onChangeFilter} />
        <Box
          css={{
            display: 'grid',
            gridTemplateColumns: 'repeat(1, 1fr)',
            gap: 32,

            '@tablet': {
              gridTemplateColumns: 'repeat(2, 1fr)',
            },

            '@laptop': {
              gridTemplateColumns: 'repeat(4, 1fr)',
            },
          }}
        >
          {filteredOperators?.map((operators) => {
            return <OperatorCard {...operators} key={operators.id} />;
          })}
        </Box>
      </Container>
    </Section>
  );
};

OperatorsSectionTemplate.templateKey = TEMPLATE_KEY;
